import { onUnmounted, ref, watch, Ref } from 'vue';
import { useCustomisationStore } from '../stores/customisation.store';
import { cleanupInjectedResources } from '../utils/stylesheetInjection.utils';
import { Document } from '@pleased-npm/fe-customisation';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

export interface ThemeEntityConfig<T> {
  /**
   * Unique ID for the stylesheet
   */
  styleId: string;

  /**
   * Prefix for external links (optional, e.g., for Google Fonts)
   */
  linkIdPrefix?: string;

  /**
   * Function to extract entity configuration from theme
   */
  extractor: (theme: Document) => T;

  /**
   * Function to apply the extracted configuration
   * Should handle both CSS injection and any external resources
   */
  applier: (config: T) => void;

  /**
   * Optional function to validate if config is valid
   */
  validator?: (config: T) => boolean;
}

/**
 * Generic base hook for injecting theme entities (fonts, shadows, borders, etc.)
 *
 * This composable provides a reusable pattern for:
 * 1. Watching the active theme
 * 2. Extracting specific configurations from the theme
 * 3. Applying those configurations (injecting CSS, external links, etc.)
 * 4. Cleaning up on unmount
 *
 * @param config - Configuration object defining how to extract and apply the theme entity
 * @returns Object containing isApplied ref indicating if the entity is loaded
 *
 * @example
 * ```ts
 * const { isApplied } = useInjectThemeEntity({
 *   styleId: 'theme-shadows',
 *   extractor: (theme) => extractShadows(theme),
 *   applier: (shadows) => injectShadowStyles(shadows),
 * });
 * ```
 */
export function useInjectThemeEntity<T>(config: ThemeEntityConfig<T>): { isApplied: Ref<boolean> } {
  const store = useCustomisationStore();
  const isApplied = ref(false);

  const { styleId, linkIdPrefix, extractor, applier, validator } = config;

  const applyEntity = (theme: any) => {
    if (!theme) {
      isApplied.value = false;
      return;
    }

    try {
      // Extract configuration from theme
      const extracted = extractor(theme);

      // Validate if validator is provided
      if (validator && !validator(extracted)) {
        logger.error(`[USE_INJECT_THEME_ENTITY] Theme entity validation failed for ${styleId}`);
        isApplied.value = false;
        return;
      }

      // Apply the configuration
      applier(extracted);

      isApplied.value = true;
    } catch (error) {
      logger.error(`[USE_INJECT_THEME_ENTITY] Error applying theme entity (${styleId}):`, {
        error: parseErrorMessage(error),
      });
      isApplied.value = false;
    }
  };

  // Watch for theme changes and apply entity
  const stopWatch = watch(
    () => store.activeTheme,
    (theme) => {
      applyEntity(theme);
    },
    { immediate: true }
  );

  // Cleanup on unmount
  onUnmounted(() => {
    stopWatch();
    cleanupInjectedResources(styleId, linkIdPrefix);
  });

  return { isApplied };
}
